
import Vue from 'vue';
import { BalanceAccount, Session, SESSION_STATE, calcRealtimeBalanceInfo, calcRunnitTokensDataForBalanceAccount } from '@run-diffusion/shared';
import { mapActions, mapGetters, mapState } from 'vuex';
import { LaunchedSessionTimerMixin } from '@/mixins';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import ExtendSessionTimerDialog from '@/components/Toolbar/SessionTimer/ExtendSessionTimerDialog.vue';

export default Vue.extend({
	name: 'SessionTimerBtns',
	mixins: [
		LaunchedSessionTimerMixin,
	],
	props: {
		waitForStoppingSession: { type: Boolean, default: true },
	},
	// TODO: When the session stops, we should show a dialog explaining why
	data () {
		return {
			SESSION_STATE,

			stoppingSession: false,
			stopSessionDialogTitle: 'Stop Session',
			stopSessionDialogBodyText: 'Shortly after this session is shutdown, the unused time will be returned to your account balance. Are you sure you want to stop this session?',
			stopSessionDialogConfirmText: 'Yes, Stop Session',
			stopSessionDialogCancelText: 'Close',
		};
	},
	computed: {
		...mapGetters([
			'companyClubOfferBenefits',
			'displayLabels',
		]),
		...mapState([
			'team',
			'teamBalanceAccount',
			'userBalanceAccount',
			'user',
		]),
		// A deactivated team can't extend its sessions (setExtendSession refuses too). Known here for the
		// selected account's team; a session of another team gets the server's answer instead.
		isSessionTeamDeactivated (): boolean {
			return !!(
				this.launchedSession?.teamId &&
				this.team &&
				this.team.id === this.launchedSession.teamId &&
				!this.team.isActive
			);
		},
		teamDeactivatedExtendMessage (): string {
			return `This ${this.displayLabels.team} is deactivated, so it can't extend sessions.`;
		},
		canGoNegative () {
			return !!(
				this.launchedSession?.teamId &&
				this.teamBalanceAccount?.type === 'TEAM' &&
				this.companyClubOfferBenefits.isEnterprise &&
				!this.team.allotmentSettings?.isBoundToBudget
			);
		},
		isOutOfTokensWithNoOtherFunds () {
			// Check if tokens are already negative AND no other payment methods available
			if (!this.canGoNegative) return false;

			const balanceAccount: BalanceAccount = this.launchedSession?.teamId ? this.teamBalanceAccount : this.userBalanceAccount;
			if (!balanceAccount) return false;

			const { realtimeBalance, realtimeRecurringBalance } = calcRealtimeBalanceInfo(balanceAccount);
			const { combinedTokens } = calcRunnitTokensDataForBalanceAccount(
				balanceAccount,
				this.user?.freemiumInfo,
				this.user?.clubOffer,
			);
			const isOutOfTokens: boolean = combinedTokens <= 0;
			const hasOtherFunds: boolean = realtimeBalance > 0 || realtimeRecurringBalance > 0;

			return isOutOfTokens && !hasOtherFunds;
		},
		showExtendBtn () {
			const CUT_OFF_IN_MINUTES: number = 60 * 30;
			return !!(
				this.launchedSession &&
				(
					// Don't show for free sessions or sessions that will put the account into further negative tokens balance (unless the timer is almost out)
					(
						this.launchedSession.purchasePrice > 0 &&
						!this.isOutOfTokensWithNoOtherFunds
					) ||
					this.countDownTimerData.seconds < CUT_OFF_IN_MINUTES
				)
			);
		},
		sessionTimerBtnsDisabled () {
			return !!(
				this.stoppingSession ||
				!this.launchedSession ||
				this.launchedSession.state !== SESSION_STATE.READY
			);
		},
	},
	methods: {
		...mapActions([
			'stopSession',
		]),
		async doStopSession (session: Session, closeAndResetCallback: Function) {
			if (session) {
				this.stoppingSession = true;

				if (this.waitForStoppingSession) {
					const { success } = await this.stopSession(session);
					if (!success) {
						// Allow to try again
						this.stoppingSession = false;
					}
				} else {
					this.stopSession(session);
					this.stoppingSession = false;
				}
			}
			if (closeAndResetCallback) {
				closeAndResetCallback();
			}
		},
	},
	components: {
		ExtendSessionTimerDialog,
		ConfirmDialog,
	},
});
